/**
 * Redux Slice for Map Other Dishes functionality
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_URL } from '../constants/apiConstants';
import axiosInstance from '../api/axios';

interface MapOtherDishesState {
  dishes: any[];
  dishcategories: any[];
  kitchensections: any[];
  recipes: any[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  savedDishes: number[];
  editingDishes: number[];
}

const initialState: MapOtherDishesState = {
  dishes: [],
  dishcategories: [],
  kitchensections: [],
  recipes: [],
  loading: false,
  error: null,
  saving: false,
  savedDishes: [],
  editingDishes: []
};


export const getEMROtherDishesDetails = createAsyncThunk(
  'mapOtherDishes/GetEMROtherDishesDetails',
  async (payload: { userid: string; useridvalue: number; dishestype: string }, { rejectWithValue }) => {
    try {
      const response: any = await axiosInstance.post(
        `${BASE_URL}/api/v1/ExceptionalMenuRequisition/GetEMROtherDishesDetails`, 
        payload
      );
      
      if (response?.data && response.data.success === false) {
        throw new Error(response.data.message || 'API request failed');
      }
      
      if (!response || !response.data) {
        throw new Error('No data received from API');
      }
      
      return response.data.data || response.data;
      
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dishes data');
    }
  }
);

export const saveUpdateOtherDishMapping = createAsyncThunk(
  'mapOtherDishes/SaveUpdateOtherDishMapping',
  async (payload: {
    mainsitecode: number;
    dishname: string;
    kitchensectioncode?: string | null;
    dishcategorycode?: string | null;
    recipecode?: string | null;
    userid: string;
    useridvalue: number;
    dishIndex: number; // Add dishIndex for state management
  }, { rejectWithValue }) => {
    try {
      // Extract dishIndex for state management, don't send to API
      const { dishIndex, ...apiPayload } = payload;
      
      const response: any = await axiosInstance.post(
        `${BASE_URL}/api/v1/ExceptionalMenuRequisition/SaveUpdateOtherDishMapping`, 
        apiPayload
      );
      
      if (response?.data && response.data.success === false) {
        throw new Error(response.data.message || 'Failed to save dish mapping');
      }
      
      if (!response || !response.data) {
        throw new Error('No response received from save API');
      }
      
      return { success: true, data: response.data, originalPayload: payload };
      
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to save dish mapping');
    }
  }
);

const mapOtherDishesSlice = createSlice({
  name: 'mapOtherDishes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDishes: (state) => {
      state.dishes = [];
      state.dishcategories = [];
      state.kitchensections = [];
      state.recipes = [];
      state.savedDishes = [];
      state.editingDishes = [];
      state.error = null;
    },
    toggleEditMode: (state, action) => {
      const dishId = action.payload;
      if (state.editingDishes.includes(dishId)) {
        // If this dish is already in edit mode, remove it (toggle off)
        state.editingDishes = state.editingDishes.filter(id => id !== dishId);
      } else {
        // Only allow one dish to be in edit mode at a time
        // Clear all existing editing dishes and set only this one
        state.editingDishes = [dishId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEMROtherDishesDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEMROtherDishesDetails.fulfilled, (state, action) => {
        state.loading = false;
        
        if (!action.payload) {
          state.dishes = [];
          state.dishcategories = [];
          state.kitchensections = [];
          state.recipes = [];
          state.savedDishes = [];
          state.editingDishes = [];
          return;
        }
        
        // Store all data arrays from the API response
        const payload = action.payload;
        state.dishes = payload.dishes || [];
        state.dishcategories = payload.dishcategories || [];
        state.kitchensections = payload.kitchensections || [];
        state.recipes = payload.recipes || [];
        
        // Initialize savedDishes based on which dishes already have mapped values
        state.savedDishes = (payload.dishes || [])
          .map((dish: any, index: number) => {
            const hasMappedValues = !!(
              (dish.dishcategorycode && dish.dishcategorycode.trim()) ||
              (dish.kitchensectioncode && dish.kitchensectioncode.trim()) ||
              (dish.recipecode && dish.recipecode.trim())
            );
            
            if (hasMappedValues) {
              // Use index for saved dishes tracking instead of mainsitecode
              return index;
            }
            return null;
          })
          .filter((id: any) => id !== null);
        
        state.editingDishes = [];
      })
      .addCase(getEMROtherDishesDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch dishes data';
        state.dishes = [];
        state.dishcategories = [];
        state.kitchensections = [];
        state.recipes = [];
      })
      .addCase(saveUpdateOtherDishMapping.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUpdateOtherDishMapping.fulfilled, (state, action) => {
        state.saving = false;
        const { originalPayload } = action.payload;
        
        // Use dishIndex directly instead of searching by mainsitecode
        const dishIndex = originalPayload.dishIndex;
        
        if (dishIndex >= 0 && dishIndex < state.dishes.length) {
          const dish = state.dishes[dishIndex];
          
          // Update the dish with the saved values
          if (originalPayload.kitchensectioncode !== undefined) {
            dish.kitchensectioncode = originalPayload.kitchensectioncode;
          }
          if (originalPayload.dishcategorycode !== undefined) {
            dish.dishcategorycode = originalPayload.dishcategorycode;
          }
          if (originalPayload.recipecode !== undefined) {
            dish.recipecode = originalPayload.recipecode;
          }
          
          state.dishes[dishIndex] = { ...dish };
          
          // Mark dish as saved using dishIndex instead of mainsitecode
          const finalId = dishIndex;
          
          if (!state.savedDishes.includes(finalId)) {
            state.savedDishes.push(finalId);
          }
          state.editingDishes = state.editingDishes.filter(id => id !== finalId);
        }
      })
      .addCase(saveUpdateOtherDishMapping.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string || 'Failed to save dish mapping';
      });
  },
});

export const { 
  clearError, 
  clearDishes,
  toggleEditMode
} = mapOtherDishesSlice.actions;

export default mapOtherDishesSlice.reducer;
